import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {

  Alert,

  Backdrop,

  Box,

  Button,

  CircularProgress,

  Paper,

  Snackbar,

  Stack,

  Typography,

} from "@mui/material";

import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { useGetWorkflowsCopyQuery } from "../../../redux/api";
import { K } from "./apiServiceI18n";

import CreateApiServiceStep1 from "./CreateApiServiceStep1";

import CustomUrlDrawer from "./CustomUrlDrawer";

import WorkflowItemsTreePanel from "./WorkflowItemsTreePanel";

import ApiServiceWizardWorkflowHost from "./ApiServiceWizardWorkflowHost";

import TranslateDataStep from "./TranslateDataStep";

import TranslateDataPromptDialog from "./TranslateDataPromptDialog";

import ApiServiceWizardMappingButton from "./ApiServiceWizardMappingButton";

import ApiServiceCancelWarningDialog from "./ApiServiceCancelWarningDialog";

import {
  loadWizardReturnContext,
  clearWizardReturnContext,
  isWizardReturnStep,
} from "./apiServiceWizardSession";

import { validateApiOrWorkflowName, validateExecuteWithin, toMilliseconds } from "./validateApiServiceWizard";
import { buildApiServiceBaseUrlPreview } from "./utils/apiServiceUtils";

import {

  createWorkflowTemplateStep1,

  deployApiServiceWizard,

  isWorkbenchSuccess,

  getWorkbenchMessage,

  runStep1ServerValidation,

  validateApiCustomUrl,

  validateApiServiceName,

  validateApiWorkflowName,

  formatCustomUrlPublicPreview,

} from "./apiServiceApiHelpers";

import {

  WIZARD_STEPS,

  buildGetDataNode,

  buildSendDataNode,

  buildTemplateHeader,

  buildTranslateNode,

  planTranslateSendIds,

  buildWizardTranslatedData,

  captureWorkflowTreeSnapshot,

  findWorkflowItemByIcon,

  getActiveTreeItem,

  getBreadcrumbSegments,

  getTreeItemIds,

  isSyncResponseAdapter,

} from "./apiServiceWizardTree";



const initialForm = (workflowGroupOptions = []) => ({

  apiName: "",

  apiType: "BridgeGateHTTP",

  description: "",

  workflowGroup: workflowGroupOptions[0] || "",

  workflowName: "",

  workflowGroupOptions,

  customUrl: { from: "", params: [] },

  conformance: false,

  conformanceExecute: false,

  executeWithin: "",

  executeUnit: "ms",

  hasNoErrors: false,

});



const initialTranslateForm = () => ({

  workflowGroup: "",

  inboundTemplate: "",

  outboundTemplate: "",

});



/**

 * API/Service creation wizard — Steps 1–4 (Create, Get Data, Translate Data, Send Data).

 */

export default function ApiServiceCreationWizard({

  onClose,

  onCancelClick,

  onOpenSaveChanges,

  onUnsavedListChange,

  registerDialogApi,

  /** When set, Overview / Performance exit the wizard to the main manager page. */
  onRequestMainTab,

}) {

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const store = useStore();

  const { account, baseUrl } = useSelector((s) => s.queryParams);

  const workflowNameTouched = useRef(false);

  const formRevision = useRef(0);



  const workflowsQuery = useGetWorkflowsCopyQuery({ account }, { skip: !account });



  const workflowGroupOptions = useMemo(() => {

    const d = workflowsQuery.data;

    if (d?.current_state === "SUCCESS" && Array.isArray(d?.data?.workflow_groups)) {

      return d.data.workflow_groups.map((g) => g?.name).filter(Boolean);

    }

    return [];

  }, [workflowsQuery.data]);



  const [mainTab, setMainTab] = useState("overview");

  const [wizardStep, setWizardStep] = useState(WIZARD_STEPS.CREATE);

  const [hasTranslate, setHasTranslate] = useState(false);

  const [form, setForm] = useState(() => initialForm([]));

  const [translateForm, setTranslateForm] = useState(initialTranslateForm);

  const [translateErrors, setTranslateErrors] = useState({});

  const [memSnapshots, setMemSnapshots] = useState({});

  const [translatePromptOpen, setTranslatePromptOpen] = useState(false);

  const [errors, setErrors] = useState({});

  const [dirty, setDirty] = useState(false);

  const [customDrawerOpen, setCustomDrawerOpen] = useState(false);

  const [pendingTab, setPendingTab] = useState(null);

  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const [nextLoading, setNextLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [validatingField, setValidatingField] = useState(null);

  const [cancelWarnOpen, setCancelWarnOpen] = useState(false);

  const step1RevisionAtGetData = useRef(null);

  const wizardRestored = useRef(false);

  const workflowsLoading = workflowsQuery.isLoading || workflowsQuery.isFetching;

  const isBusy = nextLoading || saveLoading || Boolean(validatingField);

  const showBlockingOverlay = nextLoading || saveLoading;


  useEffect(() => {

    if (!workflowGroupOptions.length) return;

    setForm((f) => {

      if (f.workflowGroup && workflowGroupOptions.includes(f.workflowGroup)) return f;

      return {

        ...f,

        workflowGroup: workflowGroupOptions[0],

        workflowGroupOptions,

      };

    });

  }, [workflowGroupOptions]);



  useEffect(() => {

    setForm((f) => ({ ...f, workflowGroupOptions }));

  }, [workflowGroupOptions]);



  useEffect(() => {

    if (wizardRestored.current) return;

    const ctx = loadWizardReturnContext();

    if (!ctx || !isWizardReturnStep(ctx.step)) return;

    wizardRestored.current = true;

    if (ctx.form) {

      setForm((prev) => ({

        ...prev,

        ...ctx.form,

        workflowGroupOptions: prev.workflowGroupOptions,

      }));

    }

    if (ctx.translateData) setTranslateForm(ctx.translateData);

    if (ctx.memSnapshots) setMemSnapshots(ctx.memSnapshots);

    if (ctx.hasTranslate !== undefined) setHasTranslate(Boolean(ctx.hasTranslate));

    setWizardStep(WIZARD_STEPS.TRANSLATE_DATA);

    setDirty(true);

  }, []);



  const invalidateDownstreamSnapshots = useCallback(() => {

    setMemSnapshots({});

    setHasTranslate(false);

    setTranslateForm(initialTranslateForm());

  }, []);



  const mergeForm = useCallback(

    (patch) => {

      setDirty(true);

      formRevision.current += 1;

      if (wizardStep !== WIZARD_STEPS.CREATE) {

        invalidateDownstreamSnapshots();

      }

      setForm((prev) => ({ ...prev, ...patch }));

    },

    [wizardStep, invalidateDownstreamSnapshots]

  );



  const handleField = useCallback(

    (field, value) => {

      if (field === "workflowName") {

        workflowNameTouched.current = true;

      }

      if (field === "conformance" && !value) {

        mergeForm({

          conformance: false,

          conformanceExecute: false,

          executeWithin: "",

          hasNoErrors: false,

        });

        return;

      }

      mergeForm({ [field]: value });

    },

    [mergeForm]

  );



  useEffect(() => {

    if (workflowNameTouched.current) return;

    const name = (form.apiName || "").trim();

    setForm((f) => {

      if (f.workflowName === name) return f;

      return { ...f, workflowName: name };

    });

  }, [form.apiName]);



  const bridgeRoot = (baseUrl || "").replace(/\/$/, "") || "/bridgegate";



  const baseUrlPreview = useMemo(
    () =>
      buildApiServiceBaseUrlPreview({
        account,
        workflowGroup: form.workflowGroup,
        workflowName: form.workflowName,
      }),
    [account, form.workflowGroup, form.workflowName]
  );



  const customUrlSummary = useMemo(() => {
    return formatCustomUrlPublicPreview(form.customUrl?.from);
  }, [form.customUrl?.from]);



  const conformanceDisabled = !form.conformance;



  const wizardUnsavedList = useMemo(() => {

    const hasProgress = dirty || wizardStep !== WIZARD_STEPS.CREATE;

    if (!hasProgress || !form.workflowName?.trim() || !form.workflowGroup) {

      return [];

    }

    const path = `${account}/${form.workflowGroup}/${form.workflowName}`;

    return [

      {

        path,

        account,

        workflow_group: form.workflowGroup,

        template: form.workflowName,

        template_type: "workflow",

        warnings: [],

        saved: false,

      },

    ];

  }, [dirty, wizardStep, account, form.workflowGroup, form.workflowName]);



  const treeItems = useMemo(

    () => getTreeItemIds(wizardStep, { hasTranslate }),

    [wizardStep, hasTranslate]

  );



  const activeTreeItem = getActiveTreeItem(wizardStep);

  const breadcrumb = getBreadcrumbSegments(wizardStep, { hasTranslate });



  const buildGetDataTreeState = useCallback(

    (getDataOverride) => {

      const root = buildWizardTranslatedData(form, {

        includeGetData: true,

        getDataNode: getDataOverride,

      });

      const getDataNode = findWorkflowItemByIcon(root, "GET_DATA");

      return {

        translatedData: root,

        templateHeader: buildTemplateHeader(form),

        selectedNode: getDataNode || {},

      };

    },

    [form]

  );



  const getDataTreeState = useMemo(() => {

    if (memSnapshots.getData) return memSnapshots.getData;

    return buildGetDataTreeState();

  }, [memSnapshots.getData, buildGetDataTreeState]);



  const sendDataTreeState = useMemo(() => {

    if (memSnapshots.sendData) return memSnapshots.sendData;

    const getDataNode =

      findWorkflowItemByIcon(getDataTreeState.translatedData, "GET_DATA") ||

      buildGetDataNode(form, buildWizardTranslatedData(form, { includeGetData: false })[0]);

    const sync = isSyncResponseAdapter(form.apiType) && getDataNode?.SYNCHRONOUS === "TRUE";

    const translateSendIds = hasTranslate ? planTranslateSendIds(getDataNode) : null;

    const translateNode = hasTranslate

      ? buildTranslateNode({

          account,

          workflowGroup: translateForm.workflowGroup,

          inboundTemplate: translateForm.inboundTemplate,

          outboundTemplate: translateForm.outboundTemplate,

          siblingNodes: [getDataNode],

          getDataNode,

          outboundId: translateSendIds?.outboundId,

        })

      : null;

    const siblingNodes = translateNode ? [getDataNode, translateNode] : [getDataNode];

    const sendNode = buildSendDataNode({

      getDataNode,

      syncResponse: sync,

      siblingNodes,

      translateNode,

      outboundTemplate: translateForm.outboundTemplate,

    });

    const translatedData = buildWizardTranslatedData(form, {

      includeGetData: true,

      getDataNode,

      includeTranslate: hasTranslate,

      translateNode,

      includeSendData: true,

      sendDataNode: sendNode,

      syncResponse: sync,

    });

    return {

      translatedData,

      templateHeader: getDataTreeState.templateHeader,

      selectedNode: findWorkflowItemByIcon(translatedData, "SEND_DATA") || {},

    };

  }, [memSnapshots.sendData, getDataTreeState, form, account, translateForm, hasTranslate]);



  const hasUnsavedChanges = wizardUnsavedList.length > 0;



  const requestTabChange = (next) => {

    if (onRequestMainTab) {

      if (hasUnsavedChanges) {

        setPendingTab(next);

      }

      onRequestMainTab(next);

      return;

    }

    if (hasUnsavedChanges && next !== mainTab) {

      setPendingTab(next);

      onOpenSaveChanges?.();

      return;

    }

    setMainTab(next);

  };



  const applyPendingTab = useCallback(() => {

    setPendingTab((pt) => {

      if (!pt) return null;

      if (onRequestMainTab) {

        onRequestMainTab(pt);

      } else {

        setMainTab(pt);

      }

      return null;

    });

  }, [onRequestMainTab]);



  const runClientValidation = () => {

    const e = {};

    const vName = validateApiOrWorkflowName(form.apiName);

    if (!vName.ok) e.apiName = vName.message;

    const vWf = validateApiOrWorkflowName(form.workflowName);

    if (!vWf.ok) e.workflowName = vWf.message;

    if (!form.apiType) e.apiType = t(K.API_SVC_REQUIRED);

    if (!(form.description || "").trim()) e.description = t(K.API_SVC_REQUIRED);

    if (!form.workflowGroup) e.workflowGroup = t(K.API_SVC_SELECT_WF_GROUP);

    if (form.conformance && form.conformanceExecute) {

      const ex = validateExecuteWithin(form.executeWithin);

      if (!ex.ok) e.executeWithin = ex.message;

    }

    setErrors(e);

    return Object.keys(e).length === 0;

  };



  const validateTranslateForm = () => {

    const e = {};

    if (!translateForm.workflowGroup) e.workflowGroup = t(K.API_SVC_SELECT_WF_GROUP);

    if (!translateForm.inboundTemplate) e.inboundTemplate = t(K.API_SVC_SELECT_INBOUND);

    if (!translateForm.outboundTemplate) e.outboundTemplate = t(K.API_SVC_SELECT_OUTBOUND);

    setTranslateErrors(e);

    return Object.keys(e).length === 0;

  };



  const setFieldError = (field, message) => {

    setErrors((prev) => {

      const next = { ...prev };

      if (message) next[field] = message;

      else delete next[field];

      return next;

    });

  };



  const handleBlurApiName = async () => {

    const client = validateApiOrWorkflowName(form.apiName);

    if (!client.ok) {

      setFieldError("apiName", client.message);

      return;

    }

    if (!account) return;

    setValidatingField("apiName");

    try {

      const result = await validateApiServiceName(dispatch, {

        account,

        serviceName: form.apiName,

      });

      setFieldError("apiName", result.ok ? null : result.message);

    } finally {

      setValidatingField(null);

    }

  };



  const handleBlurWorkflowName = async () => {

    const client = validateApiOrWorkflowName(form.workflowName);

    if (!client.ok) {

      setFieldError("workflowName", client.message);

      return;

    }

    if (!account || !form.workflowGroup) return;

    setValidatingField("workflowName");

    try {

      const result = await validateApiWorkflowName(dispatch, {

        account,

        workflowGroup: form.workflowGroup,

        workflowName: form.workflowName,

      });

      setFieldError("workflowName", result.ok ? null : result.message);

    } finally {

      setValidatingField(null);

    }

  };



  const handleCustomUrlApply = async (customUrl) => {
    let normalizedKey;

    if (form.apiType === "BridgeGateHTTP" && account) {
      setValidatingField("customUrl");
      try {
        const result = await validateApiCustomUrl(dispatch, {
          account,
          customUrl,
          workflowGroup: form.workflowGroup,
          workflowName: form.workflowName,
        });

        if (!result.ok) {
          setFieldError("customUrl", result.message);
          return { ok: false, message: result.message };
        }

        normalizedKey = result.normalizedKey;
        setFieldError("customUrl", null);
      } finally {
        setValidatingField(null);
      }
    }

    mergeForm({ customUrl: { ...customUrl, normalizedKey } });
    return { ok: true, normalizedKey };
  };



  const handleCancelRequest = useCallback(() => {

    if (saveLoading || nextLoading) return;

    if (onCancelClick) {

      onCancelClick();

      return;

    }

    if (hasUnsavedChanges) {

      setCancelWarnOpen(true);

      return;

    }

    onClose?.();

  }, [hasUnsavedChanges, saveLoading, nextLoading, onClose, onCancelClick]);



  const goToGetData = () => {

    step1RevisionAtGetData.current = formRevision.current;

    setWizardStep(WIZARD_STEPS.GET_DATA);

    setMemSnapshots((prev) => ({ ...prev, getData: undefined, sendData: undefined }));

  };



  const handleCreateNext = async () => {

    if (!runClientValidation()) return;

    if (!account) {

      setSnack({ open: true, severity: "error", message: t(K.API_SVC_ACCOUNT_REQUIRED) });

      return;

    }

    setNextLoading(true);

    try {

      const validation = await runStep1ServerValidation(dispatch, form, { account });

      if (!validation.ok) {

        setFieldError(validation.field, validation.message);

        setSnack({ open: true, severity: "warning", message: validation.message });

        return;

      }



      const createRes = await createWorkflowTemplateStep1(dispatch, {

        account,

        workflowGroup: form.workflowGroup,

        workflowName: form.workflowName,

      });



      if (!isWorkbenchSuccess(createRes)) {

        setSnack({

          open: true,

          severity: "error",

          message: getWorkbenchMessage(createRes, t(K.API_SVC_CREATE_WF_FAILED)),

        });

        return;

      }



      const ms = toMilliseconds(form.executeWithin || "1", form.executeUnit);

      void ms;

      invalidateDownstreamSnapshots();

      goToGetData();

      setDirty(true);

    } catch (err) {

      setSnack({

        open: true,

        severity: "error",

        message: err?.message || t(K.API_SVC_STEP1_FAILED),

      });

    } finally {

      setNextLoading(false);

    }

  };



  const captureGetDataFromRedux = useCallback(() => {
    const wf = store.getState().treeData.workflow;
    return captureWorkflowTreeSnapshot(wf);
  }, [store]);

  const captureWorkflowSnapshotForSave = useCallback(() => {
    if (wizardStep === WIZARD_STEPS.SEND_DATA) {
      const live = captureGetDataFromRedux();
      if (live?.translatedData?.length) return live;
    }
    const cached = memSnapshots.sendData || memSnapshots.getData;
    if (cached?.translatedData?.length) return cached;
    return captureGetDataFromRedux();
  }, [wizardStep, memSnapshots.sendData, memSnapshots.getData, captureGetDataFromRedux]);

  const handleGetDataApply = () => {
    const snap = captureGetDataFromRedux();
    setDirty(true);
    setMemSnapshots((prev) => ({ ...prev, getData: snap }));
    setTranslatePromptOpen(true);
  };



  const proceedAfterGetData = (wantTranslate) => {

    setTranslatePromptOpen(false);

    setHasTranslate(wantTranslate);

    const snap = memSnapshots.getData || captureGetDataFromRedux();

    const getDataNode = findWorkflowItemByIcon(snap.translatedData, "GET_DATA");

    if (isSyncResponseAdapter(form.apiType) && getDataNode) {

      getDataNode.SYNCHRONOUS = "TRUE";

      if (getDataNode.INBOUND) getDataNode.INBOUND.SYNCHRONOUS = "TRUE";
      if (form.apiType === "BridgeGateHTTP") getDataNode.INBOUND.BTN_SEND_SYNC_RESPONSE = "TRUE";

    }
    
    const sync = isSyncResponseAdapter(form.apiType);

    const sendNode = buildSendDataNode({

      getDataNode,

      syncResponse: sync,

      siblingNodes: [getDataNode],

    });

    const translatedData = buildWizardTranslatedData(form, {

      includeGetData: true,

      getDataNode,

      includeSendData: true,

      sendDataNode: sendNode,

      syncResponse: sync,

    });

    if (wantTranslate) {

      setMemSnapshots((prev) => ({ ...prev, getData: { ...snap, selectedNode: getDataNode, translatedData } }));

      setTranslateForm((prev) => ({

        ...prev,

        workflowGroup: prev.workflowGroup || "",

      }));

      setWizardStep(WIZARD_STEPS.TRANSLATE_DATA);

      return;

    }

    setMemSnapshots((prev) => ({

      ...prev,

      getData: {

        ...snap,
        
        translatedData,
        selectedNode: getDataNode,

      },

      sendData: {

        translatedData,

        templateHeader: snap.templateHeader,

        selectedNode: sendNode,

      },

    }));

    setWizardStep(WIZARD_STEPS.SEND_DATA);

  };



  const handleTranslateNext = () => {

    if (!validateTranslateForm()) return;

    const getSnap = memSnapshots.getData || buildGetDataTreeState();

    const getDataNode = findWorkflowItemByIcon(getSnap.translatedData, "GET_DATA");

    const translateSendIds = planTranslateSendIds(getDataNode);

    const translateNode = buildTranslateNode({

      account,

      workflowGroup: translateForm.workflowGroup,

      inboundTemplate: translateForm.inboundTemplate,

      outboundTemplate: translateForm.outboundTemplate,

      siblingNodes: [getDataNode],

      getDataNode,

      outboundId: translateSendIds.outboundId,

    });

    const sendNode = buildSendDataNode({

      getDataNode,

      syncResponse: false,

      siblingNodes: [getDataNode, translateNode],

      translateNode,

      outboundTemplate: translateForm.outboundTemplate,

    });

    const translatedData = buildWizardTranslatedData(form, {

      includeGetData: true,

      getDataNode,

      includeTranslate: true,

      translateNode,

      includeSendData: true,

      sendDataNode: sendNode,

    });

    setMemSnapshots((prev) => ({

      ...prev,

      translateData: { ...translateForm },

      sendData: {

        translatedData,

        templateHeader: getSnap.templateHeader,

        selectedNode: sendNode,

      },

    }));

    setWizardStep(WIZARD_STEPS.SEND_DATA);

  };



  const handlePrevious = () => {

    if (wizardStep === WIZARD_STEPS.GET_DATA) {

      if (step1RevisionAtGetData.current !== formRevision.current) {

        invalidateDownstreamSnapshots();

      }

      setWizardStep(WIZARD_STEPS.CREATE);

      return;

    }

    if (wizardStep === WIZARD_STEPS.TRANSLATE_DATA) {

      setWizardStep(WIZARD_STEPS.GET_DATA);

      setHasTranslate(false);

      setMemSnapshots((prev) => {

        const next = { ...prev };

        delete next.translateData;

        delete next.sendData;

        return next;

      });

      return;

    }

    if (wizardStep === WIZARD_STEPS.SEND_DATA) {

      if (hasTranslate) {

        setWizardStep(WIZARD_STEPS.TRANSLATE_DATA);

      } else {

        setWizardStep(WIZARD_STEPS.GET_DATA);

      }

      setMemSnapshots((prev) => {

        const next = { ...prev };

        delete next.sendData;

        return next;

      });

    }

  };



  const handleFinalSave = async () => {
    if (!account) {
      setSnack({ open: true, severity: "error", message: t(K.API_SVC_ACCOUNT_REQUIRED) });
      return;
    }
    if (!form.workflowGroup?.trim() || !form.workflowName?.trim()) {
      setSnack({ open: true, severity: "error", message: t(K.API_SVC_WF_REQUIRED) });
      return;
    }

    const workflowSnapshot = captureWorkflowSnapshotForSave();
    if (!workflowSnapshot?.translatedData?.length) {
      setSnack({
        open: true,
        severity: "error",
        message: t(K.API_SVC_NO_WF_TO_SAVE),
      });
      return;
    }

    setSaveLoading(true);
    try {
      const result = await deployApiServiceWizard(dispatch, {
        account,
        form,
        workflowSnapshot,
      });

      if (!result.ok) {
        setSnack({
          open: true,
          severity: "error",
          message: result.message || t(K.API_SVC_SAVE_FAILED),
        });
        return;
      }

      setSnack({
        open: true,
        severity: "success",
        message: t(K.API_SVC_SAVE_SUCCESS),
      });
      setDirty(false);
      clearWizardReturnContext();
      onClose?.();
    } catch (err) {
      setSnack({
        open: true,
        severity: "error",
        message: err?.message || t(K.API_SVC_SAVE_REQUEST_FAILED),
      });
    } finally {
      setSaveLoading(false);
    }
  };



  const handleDiscardWizard = useCallback(() => {

    setPendingTab(null);

    setForm(initialForm(workflowGroupOptions));

    workflowNameTouched.current = false;

    formRevision.current = 0;

    setWizardStep(WIZARD_STEPS.CREATE);

    setHasTranslate(false);

    setTranslateForm(initialTranslateForm());

    setMemSnapshots({});

    setDirty(false);

    clearWizardReturnContext();

    onClose?.();

  }, [onClose, workflowGroupOptions]);



  useEffect(() => {

    onUnsavedListChange?.(wizardUnsavedList);

  }, [wizardUnsavedList, onUnsavedListChange]);



  useEffect(() => {

    if (!registerDialogApi) return;

    registerDialogApi({

      discard: handleDiscardWizard,

      onSaveDialogClose: () => setPendingTab(null),

      onSaveDialogSuccess: () => {

        setDirty(false);

        applyPendingTab();

      },

    });

  }, [registerDialogApi, handleDiscardWizard, applyPendingTab]);



  const showMapping =

    hasTranslate &&

    translateForm.workflowGroup &&

    translateForm.inboundTemplate &&

    translateForm.outboundTemplate;



  const stepContent = () => {

    if (wizardStep === WIZARD_STEPS.CREATE) {

      return (

        <CreateApiServiceStep1

          values={form}

          onChange={handleField}

          errors={errors}

          baseUrlPreview={baseUrlPreview}

          customUrlSummary={customUrlSummary}

          onOpenCustomUrl={() => setCustomDrawerOpen(true)}

          onBlurApiName={handleBlurApiName}

          onBlurWorkflowName={handleBlurWorkflowName}

          validatingField={validatingField}

          conformanceDisabled={conformanceDisabled}

          workflowsLoading={workflowsLoading}

        />

      );

    }

    if (wizardStep === WIZARD_STEPS.GET_DATA) {

      return (

        <ApiServiceWizardWorkflowHost
          key="get-data"
          hostKey="get-data"
          account={account}
          form={form}
          treeState={getDataTreeState}
        />

      );

    }

    if (wizardStep === WIZARD_STEPS.TRANSLATE_DATA) {

      return (

        <TranslateDataStep

          account={account}

          value={translateForm}

          onChange={(next) => {

            setDirty(true);

            setTranslateForm(next);

            setMemSnapshots((prev) => {

              const copy = { ...prev };

              delete copy.sendData;

              return copy;

            });

          }}

          errors={translateErrors}

        />

      );

    }

    if (wizardStep === WIZARD_STEPS.SEND_DATA) {

      return (

        <ApiServiceWizardWorkflowHost
          key="send-data"
          hostKey="send-data"
          account={account}
          form={form}
          treeState={sendDataTreeState}
        />

      );

    }

    return null;

  };



  const renderActionButtons = () => {

    if (wizardStep === WIZARD_STEPS.CREATE) {

      return (

        <>

          <Button

            variant="contained"

            color="primary"

            onClick={handleCreateNext}

            disabled={nextLoading || saveLoading || workflowsLoading || mainTab !== "overview"}

            startIcon={nextLoading ? <CircularProgress size={16} color="inherit" /> : null}

          >

            {nextLoading ? t(K.API_SVC_BTN_CREATING) : t(K.BTN_NEXT)}

          </Button>

          <Button variant="outlined" onClick={handleCancelRequest} disabled={saveLoading || nextLoading}>

            {t(K.BTN_CANCEL)}

          </Button>

        </>

      );

    }



    const prevBtn = (

      <Button
        variant="contained"
        onClick={handlePrevious}
        disabled={saveLoading || nextLoading}
        sx={{ textTransform: "none" }}
      >

        {t(K.API_SVC_BTN_PREVIOUS)}

      </Button>

    );

    const cancelBtn = (

      <Button variant="outlined" onClick={handleCancelRequest} disabled={saveLoading || nextLoading}>

        {t(K.BTN_CANCEL)}

      </Button>

    );

    const mappingBtn =

      showMapping && wizardStep !== WIZARD_STEPS.GET_DATA ? (

        <ApiServiceWizardMappingButton

          translateData={translateForm}

          form={form}

          wizardStep={wizardStep}

          memSnapshots={memSnapshots}

          hasTranslate={hasTranslate}

        />

      ) : null;



    if (wizardStep === WIZARD_STEPS.GET_DATA) {

      return (

        <>

          {prevBtn}

          <Button
            variant="contained"
            onClick={handleGetDataApply}
            disabled={saveLoading || nextLoading}
            sx={{ textTransform: "none" }}
          >

            {t(K.BTN_APPLY)}

          </Button>

          {cancelBtn}

        </>

      );

    }

    if (wizardStep === WIZARD_STEPS.TRANSLATE_DATA) {

      return (

        <>

          {mappingBtn}

          {prevBtn}

          <Button
            variant="contained"
            onClick={handleTranslateNext}
            disabled={saveLoading || nextLoading}
            sx={{ textTransform: "none" }}
          >

            {t(K.BTN_NEXT)}

          </Button>

          {cancelBtn}

        </>

      );

    }

    if (wizardStep === WIZARD_STEPS.SEND_DATA) {

      return (

        <>

          {mappingBtn}

          {prevBtn}

          <Button
            variant="contained"
            onClick={handleFinalSave}
            disabled={saveLoading || nextLoading}
            startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none" }}
          >
            {saveLoading ? t(K.API_SVC_BTN_SAVING) : t(K.API_SVC_BTN_SAVE)}
          </Button>

          {cancelBtn}

        </>

      );

    }

    return null;

  };



  const overviewContent = (

    <GridRow>

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>

        {wizardStep !== WIZARD_STEPS.CREATE ? (

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flexShrink: 0 }}>

            {breadcrumb.join(" > ")}

          </Typography>

        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            pointerEvents: showBlockingOverlay ? "none" : "auto",
            opacity: showBlockingOverlay ? 0.6 : 1,
          }}
        >
          {stepContent()}
        </Box>

      </Box>

      <Box

        sx={{

          width: { xs: "100%", md: 320 },

          flexShrink: 0,

          alignSelf: "stretch",

          display: "flex",

          flexDirection: "column",

          minHeight: 0,

        }}

      >

        <WorkflowItemsTreePanel

          workflowLabel={form.workflowName || form.apiName}

          items={treeItems}

          activeItem={activeTreeItem}

        />

      </Box>

    </GridRow>

  );



  return (

    <Box

      sx={{

        p: 2,

        width: "100%",

        maxWidth: "100%",

        flex: 1,

        display: "flex",

        flexDirection: "column",

        overflow: "hidden",

        minHeight: 0,

        boxSizing: "border-box",

        position: "relative",

      }}

    >

      <Backdrop
        open={showBlockingOverlay}
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 2,
          color: "#fff",
          flexDirection: "column",
          gap: 2,
          bgcolor: "rgba(0, 0, 0, 0.55)",
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {saveLoading ? t(K.API_SVC_SAVING_API) : t(K.API_SVC_CREATING_WORKFLOW)}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {saveLoading
            ? t(K.API_SVC_WAIT_DO_NOT_CLOSE)
            : t(K.API_SVC_WAIT_CREATING)}
        </Typography>
      </Backdrop>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>

        <Stack direction="row" spacing={1} role="tablist">

          <Button

            role="tab"

            aria-selected={mainTab === "overview"}

            variant={mainTab === "overview" ? "contained" : "outlined"}

            onClick={() => requestTabChange("overview")}

            disabled={isBusy}

            sx={{ textTransform: "none", minWidth: 100 }}

          >

            {t(K.API_SVC_TAB_OVERVIEW)}

          </Button>

          <Button

            role="tab"

            aria-selected={mainTab === "performance"}

            variant={mainTab === "performance" ? "contained" : "outlined"}

            onClick={() => requestTabChange("performance")}

            disabled={isBusy}

            sx={{ textTransform: "none", minWidth: 100 }}

          >

            {t(K.API_SVC_TAB_PERFORMANCE)}

          </Button>

        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">

          {renderActionButtons()}

        </Stack>

      </Stack>



      <Paper

        variant="outlined"

        sx={{

          p: 2,

          flex: 1,

          minHeight: 0,

          width: "100%",

          boxSizing: "border-box",

          display: "flex",

          flexDirection: "column",

          overflow: "hidden",

        }}

      >

        {mainTab === "overview" && (

          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>

            {overviewContent}

          </Box>

        )}

        {mainTab === "performance" && !onRequestMainTab && (

          <Typography color="text.secondary" sx={{ py: 4 }}>

            {t(K.API_SVC_PERF_BODY)}

          </Typography>

        )}

      </Paper>



      <CustomUrlDrawer
        open={customDrawerOpen}
        onClose={() => setCustomDrawerOpen(false)}
        value={form.customUrl}
        baseUrlTo={baseUrlPreview}
        onApply={handleCustomUrlApply}
        applyLoading={validatingField === "customUrl"}
      />



      <ApiServiceCancelWarningDialog

        open={cancelWarnOpen}

        onClose={() => setCancelWarnOpen(false)}

        onSaveChanges={() => setCancelWarnOpen(false)}

        onConfirmCancel={() => {

          setCancelWarnOpen(false);

          handleDiscardWizard();

        }}

      />



      <TranslateDataPromptDialog

        open={translatePromptOpen}

        onClose={() => setTranslatePromptOpen(false)}

        onYes={() => proceedAfterGetData(true)}

        onNo={() => proceedAfterGetData(false)}

      />



      <Snackbar

        open={snack.open}

        autoHideDuration={6000}

        onClose={() => setSnack((s) => ({ ...s, open: false }))}

        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}

      >

        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>

          {snack.message}

        </Alert>

      </Snackbar>

    </Box>

  );

}



function GridRow({ children }) {

  return (

    <Stack

      direction={{ xs: "column", md: "row" }}

      spacing={2}

      alignItems="stretch"

      sx={{ flex: 1, minHeight: 0, width: "100%" }}

    >

      {children}

    </Stack>

  );

}


